import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useGql } from '@ah/design';
import { Header, DetailSkeleton, PullToRefresh, BottomSheet } from '@ah/design/mobile';
import { QRCodeSVG } from 'qrcode.react';
import {
  CalendarDays,
  Check,
  Clock,
  Copy,
  Loader2,
  Lock,
  MapPin,
  Monitor,
  Send,
  Video,
  Users,
} from 'lucide-react';
import {
  GATHERING_QUERY,
  GATHERING_DETAIL_RESERVE_MUTATION,
  GATHERING_DETAIL_CANCEL_MUTATION,
  type Gathering,
} from '../shared/index.ts';
import { MY_PUSH_DEVICE_TYPES_QUERY } from '@ah/notifications/shared';

const SEND_GATHERING_LINK_TO_MY_DEVICES = /* GraphQL */ `
  mutation SendGatheringLinkToMyDevices($gatheringId: ID!) {
    sendGatheringLinkToMyDevices(gatheringId: $gatheringId)
  }
`;

interface GatheringQueryResponse {
  gathering: Gathering;
  me: { id: string; email: string | null } | null;
}

export function GatheringDetail() {
  const { id } = useParams<{ id: string }>();
  const gql = useGql();
  const qc = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['gatherings', id],
    queryFn: async () => {
      return gql.request<GatheringQueryResponse>(GATHERING_QUERY, { id });
    },
    enabled: !!id,
  });
  const gathering = data?.gathering;
  const me = data?.me ?? null;

  const reserve = useMutation({
    mutationFn: (email?: string | null) =>
      gql.request(GATHERING_DETAIL_RESERVE_MUTATION, { gatheringId: id, email: email ?? null }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gatherings', id] }),
  });
  const cancel = useMutation({
    mutationFn: () => gql.request(GATHERING_DETAIL_CANCEL_MUTATION, { gatheringId: id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['gatherings', id] }),
  });

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [joinSheetOpen, setJoinSheetOpen] = useState(false);

  function openConfirm() {
    setEmailInput(me?.email ?? '');
    setEmailError(null);
    setConfirmOpen(true);
  }

  async function handleSendInvite() {
    const trimmed = emailInput.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    setEmailError(null);
    try {
      await reserve.mutateAsync(trimmed);
      setConfirmOpen(false);
    } catch (err) {
      setEmailError(err instanceof Error ? err.message : 'Could not RSVP. Please try again.');
    }
  }

  async function handleSkipInvite() {
    try {
      await reserve.mutateAsync(null);
      setConfirmOpen(false);
    } catch (err) {
      setEmailError(err instanceof Error ? err.message : 'Could not RSVP. Please try again.');
    }
  }

  return (
    <div className="fixed inset-0 flex flex-col bg-background">
      <Header title={gathering?.title ?? 'Gathering'} backTo="/gatherings" />
      <PullToRefresh className="flex-1 pb-24" onRefresh={() => refetch()}>
        {isLoading ? (
          <DetailSkeleton hero="wide" />
        ) : error || !gathering ? (
          <div className="px-4 py-10 text-center">
            <p className="text-sm text-destructive mb-3">Gathering not found.</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="text-sm px-3 py-1.5 rounded-full border border-border bg-card"
            >
              Retry
            </button>
          </div>
        ) : (
          <GatheringBody gathering={gathering} />
        )}
      </PullToRefresh>

      {gathering ? (
        <CtaBar
          gathering={gathering}
          reserving={reserve.isPending}
          cancelling={cancel.isPending}
          onReserve={openConfirm}
          onCancel={() => cancel.mutate()}
          onJoin={() => setJoinSheetOpen(true)}
        />
      ) : null}

      {gathering ? (
        <BottomSheet
          open={joinSheetOpen}
          onClose={() => setJoinSheetOpen(false)}
          title="Join from a computer"
        >
          <JoinHandoffSheet gathering={gathering} />
        </BottomSheet>
      ) : null}

      <BottomSheet
        open={confirmOpen}
        onClose={() => !reserve.isPending && setConfirmOpen(false)}
        title="Send calendar invite"
      >
        <div className="px-4 pt-4 pb-2 space-y-3">
          <p className="text-[15px] text-foreground/80 leading-relaxed">
            Where should we send the calendar invite?
          </p>
          <input
            type="email"
            inputMode="email"
            autoComplete="email"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            placeholder="you@example.com"
            className="w-full h-12 px-4 rounded-full border border-border bg-card text-[15px] outline-none focus:border-primary"
            disabled={reserve.isPending}
          />
          {emailError ? (
            <p className="text-[13px] text-destructive">{emailError}</p>
          ) : null}
          <button
            type="button"
            onClick={() => void handleSendInvite()}
            disabled={reserve.isPending}
            className="w-full h-12 rounded-full bg-primary text-primary-foreground text-sm font-semibold active:scale-[0.98] transition-all disabled:opacity-60"
          >
            {reserve.isPending ? 'Reserving…' : 'Send invite & RSVP'}
          </button>
          <button
            type="button"
            onClick={() => void handleSkipInvite()}
            disabled={reserve.isPending}
            className="w-full h-12 rounded-full border border-border bg-card text-sm font-medium text-muted-foreground active:scale-[0.98] transition-all disabled:opacity-60"
          >
            Skip — just RSVP
          </button>
        </div>
      </BottomSheet>
    </div>
  );
}

function GatheringBody({ gathering }: { gathering: Gathering }) {
  const start = new Date(gathering.startsAt);
  const end = new Date(gathering.endsAt);
  const isOnline = gathering.kind === 'roundtable';
  const isPrivate = gathering.visibility === 'circle' && gathering.circle?.name;
  const dateLine = start.toLocaleDateString('en-US', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const timeLine = `${start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} – ${end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;

  return (
    <article className="space-y-4">
      {gathering.coverUrl ? (
        <div className="aspect-video bg-muted overflow-hidden">
          <img src={gathering.coverUrl} alt="" className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className="aspect-video relative overflow-hidden bg-muted/60">
          <div className="absolute inset-0 flex items-center justify-center text-muted-foreground/40">
            <CalendarDays size={72} strokeWidth={1.25} />
          </div>
        </div>
      )}

      <div className="px-4 space-y-3">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-wide rounded-full border border-border px-2 py-0.5 text-muted-foreground">
            {gathering.kind}
          </span>
        </div>

        <h1 className="text-xl font-bold leading-tight">{gathering.title}</h1>

        {isPrivate ? (
          <div className="rounded-2xl border border-border/60 bg-muted/30 px-3 py-2 flex items-center gap-2 text-sm">
            <Lock size={14} className="shrink-0 text-muted-foreground" />
            <span className="text-muted-foreground">
              Private — open to members of{' '}
              <Link
                to={`/circles/${gathering.circleId}`}
                className="text-foreground font-medium hover:underline"
              >
                {gathering.circle!.name}
              </Link>
              .
            </span>
          </div>
        ) : null}

        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <CalendarDays size={14} className="shrink-0" />
            <span>{dateLine}</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Clock size={14} className="shrink-0" />
            <span>{timeLine}</span>
          </div>
          {isOnline ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Video size={14} className="shrink-0" />
              <span>Online</span>
            </div>
          ) : gathering.location ? (
            <div className="flex items-start gap-2 text-muted-foreground">
              <MapPin size={14} className="shrink-0 mt-0.5" />
              <span>{gathering.location}</span>
            </div>
          ) : null}
          {(() => {
            const full = gathering.capacity != null && gathering.participantCount >= gathering.capacity;
            return (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Users size={14} className="shrink-0" />
                <span>{full ? 'Sold out' : 'A few seats remaining'}</span>
              </div>
            );
          })()}
        </div>

        {gathering.description ? (
          <section className="pt-2">
            <h2 className="text-sm font-semibold mb-1.5">About</h2>
            <p className="text-sm whitespace-pre-wrap text-muted-foreground leading-relaxed">
              {gathering.description}
            </p>
          </section>
        ) : null}

      </div>
    </article>
  );
}

function CtaBar({
  gathering,
  reserving,
  cancelling,
  onReserve,
  onCancel,
  onJoin,
}: {
  gathering: Gathering;
  reserving: boolean;
  cancelling: boolean;
  onReserve: () => void;
  onCancel: () => void;
  onJoin: () => void;
}) {
  const going = gathering.currentUserParticipant?.status === 'reserved'
    || gathering.currentUserParticipant?.status === 'GOING';
  const isFull = gathering.capacity != null && gathering.participantCount >= gathering.capacity;
  const ended = new Date(gathering.endsAt).getTime() < Date.now();
  const isOnline = gathering.kind === 'roundtable';
  const canJoin = going && isOnline && !ended;

  if (ended) return null;

  return (
    <div
      className="fixed left-0 right-0 bottom-0 z-30 bg-background border-t border-border px-3 py-2 space-y-2"
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 0.5rem)' }}
    >
      {canJoin && (
        <button
          type="button"
          onClick={onJoin}
          className="w-full rounded-full bg-primary text-primary-foreground text-sm font-semibold py-2.5 flex items-center justify-center gap-2"
        >
          <Video size={16} /> Join Gathering
        </button>
      )}
      {going ? (
        <button
          type="button"
          onClick={onCancel}
          disabled={cancelling}
          className="w-full rounded-full border border-border bg-card text-sm font-medium py-2.5 disabled:opacity-60"
        >
          {cancelling ? 'Cancelling…' : 'Cancel RSVP'}
        </button>
      ) : (
        <button
          type="button"
          onClick={onReserve}
          disabled={reserving || isFull}
          className="w-full rounded-full bg-primary text-primary-foreground text-sm font-medium py-2.5 disabled:opacity-60"
        >
          {isFull ? 'Gathering full' : reserving ? 'Reserving…' : 'RSVP'}
        </button>
      )}
    </div>
  );
}

// ── Join-handoff bottom sheet ─────────────────────────────────────────
//
// Mobile users can't join the call from here. The sheet hands them off to
// the desktop subdomain: short URL + QR + copy-link. The "send to my
// devices" affordance lands in Phase 2 once we tag push subscriptions by
// device form-factor.

function JoinHandoffSheet({ gathering }: { gathering: Gathering }) {
  const gql = useGql();
  const liveUrl = `https://desktop.underlings.com/gatherings/${gathering.id}/live`;
  const [copied, setCopied] = useState(false);
  const [sentCount, setSentCount] = useState<number | null>(null);

  // Look up the caller's push-token form factors so the "Send to my
  // devices" button only renders when there's actually somewhere bigger
  // than this phone to forward to.
  const { data: deviceTypes } = useQuery({
    queryKey: ['my-push-device-types'],
    queryFn: async () =>
      (await gql.request<{ myPushDeviceTypes: string[] }>(MY_PUSH_DEVICE_TYPES_QUERY))
        .myPushDeviceTypes,
  });
  const hasNonMobileDevice = (deviceTypes ?? []).some((t) => t === 'desktop' || t === 'tablet');

  const send = useMutation({
    mutationFn: async () => {
      const res = await gql.request<{ sendGatheringLinkToMyDevices: number }>(
        SEND_GATHERING_LINK_TO_MY_DEVICES,
        { gatheringId: gathering.id },
      );
      return res.sendGatheringLinkToMyDevices;
    },
    onSuccess: (n) => {
      setSentCount(n);
      setTimeout(() => setSentCount(null), 4000);
    },
  });

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(liveUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: ignore — user can long-press the visible URL to copy.
    }
  }

  return (
    <div className="px-4 pt-4 pb-4 space-y-4">
      <div className="rounded-2xl border border-border bg-card p-4 flex items-start gap-3">
        <Monitor size={18} className="shrink-0 mt-0.5 text-muted-foreground" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">Open on a computer or tablet</p>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Go to <span className="font-medium text-foreground">desktop.underlings.com</span>{' '}
            and the gathering will be ready to join.
          </p>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-4 flex flex-col items-center gap-3">
        <QRCodeSVG value={liveUrl} size={176} level="M" />
        <p className="text-xs text-muted-foreground text-center">
          Scan with your computer's camera (or another phone) to open the gathering.
        </p>
      </div>

      <button
        type="button"
        onClick={() => void handleCopy()}
        className="w-full h-12 rounded-full border border-border bg-card text-sm font-medium active:scale-[0.98] transition-all flex items-center justify-center gap-2"
      >
        {copied ? <><Check size={16} /> Copied</> : <><Copy size={16} /> Copy link</>}
      </button>

      {hasNonMobileDevice ? (
        <button
          type="button"
          onClick={() => send.mutate()}
          disabled={send.isPending}
          className="w-full h-12 rounded-full bg-primary text-primary-foreground text-sm font-semibold active:scale-[0.98] transition-all flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {send.isPending ? (
            <><Loader2 size={16} className="animate-spin" /> Sending…</>
          ) : sentCount != null ? (
            <><Check size={16} /> Sent to {sentCount} {sentCount === 1 ? 'device' : 'devices'}</>
          ) : (
            <><Send size={16} /> Send to my computer</>
          )}
        </button>
      ) : null}
    </div>
  );
}
